<!-- src/lib/components/ShortcutModal.svelte -->
<!--
  Keyboard shortcut cheatsheet. Triggered by ? from anywhere (not
  inside an input). Grouped by section, searchable.

  Refinement #48 from the 50-refinements spec.
-->
<script lang="ts">
  import { fly, fade } from 'svelte/transition';

  interface Props {
    onClose: () => void;
  }
  let { onClose }: Props = $props();

  let query = $state('');

  interface Shortcut { keys: string; desc: string; }
  interface Group { name: string; shortcuts: Shortcut[]; }

  const groups: Group[] = [
    {
      name: 'Navigation',
      shortcuts: [
        { keys: '⌘ K',       desc: 'Command palette' },
        { keys: '⌘ \\',      desc: 'Toggle sidebar' },
        { keys: '⌘ B',       desc: 'Toggle file explorer' },
        { keys: 'Ctrl `',    desc: 'Toggle terminal' },
        { keys: '⌘ W',       desc: 'Close active tab' },
        { keys: '⌘ ⇧ T',     desc: 'Reopen last closed tab' },
        { keys: '?',          desc: 'This cheatsheet' },
      ],
    },
    {
      name: 'Cards',
      shortcuts: [
        { keys: '/',          desc: 'Filter (board / canvas)' },
        { keys: 'N',          desc: 'New task' },
        { keys: 'F',          desc: 'Focus mode (card open)' },
        { keys: 'Esc',        desc: 'Close card drawer' },
        { keys: 'Del / Backspace', desc: 'Delete selected item' },
      ],
    },
    {
      name: 'Studio',
      shortcuts: [
        { keys: 'V',          desc: 'Select tool' },
        { keys: 'R',          desc: 'Rectangle tool' },
        { keys: 'O',          desc: 'Ellipse tool' },
        { keys: 'T',          desc: 'Text tool' },
        { keys: 'F',          desc: 'Frame tool' },
        { keys: 'Space + drag', desc: 'Pan canvas' },
        { keys: '⌘ + scroll', desc: 'Zoom canvas (around cursor)' },
        { keys: 'Delete',     desc: 'Delete selected node' },
      ],
    },
    {
      name: 'Development',
      shortcuts: [
        { keys: '⌘ B',       desc: 'Toggle file explorer' },
        { keys: 'Ctrl `',    desc: 'Toggle terminal' },
      ],
    },
  ];

  const filtered = $derived((() => {
    if (!query.trim()) return groups;
    const q = query.toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        shortcuts: g.shortcuts.filter(
          (s) => s.desc.toLowerCase().includes(q) || s.keys.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.shortcuts.length > 0);
  })());

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="backdrop" transition:fade={{ duration: 120 }} onclick={onClose}>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="modal" transition:fly={{ y: 20, duration: 180, opacity: 0 }} onclick={(e) => e.stopPropagation()}>
    <header class="modal-header">
      <h2>Keyboard shortcuts</h2>
      <input
        class="search"
        type="text"
        placeholder="Search shortcuts…"
        bind:value={query}
      />
    </header>
    <div class="modal-body">
      {#each filtered as group (group.name)}
        <section class="shortcut-group">
          <h3>{group.name}</h3>
          <div class="shortcut-list">
            {#each group.shortcuts as s (s.keys + s.desc)}
              <div class="shortcut-row">
                <kbd>{s.keys}</kbd>
                <span>{s.desc}</span>
              </div>
            {/each}
          </div>
        </section>
      {/each}
      {#if filtered.length === 0}
        <p class="no-results">No shortcuts match "{query}".</p>
      {/if}
    </div>
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.50);
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal {
    width: 520px;
    max-height: 80vh;
    background: oklch(0.095 0.018 275);
    border: 1px solid oklch(0.35 0.06 275 / 0.45);
    border-radius: 10px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.50);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .modal-header {
    padding: 1.25rem 1.5rem 0.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.25);
  }
  .modal-header h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: oklch(0.94 0.08 310);
  }
  .search {
    font-size: 0.78rem;
    padding: 0.35rem 0.65rem;
    background: oklch(0.065 0.015 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 4px;
    color: oklch(0.92 0.06 310);
    outline: none;
    width: 180px;
  }
  .search:focus {
    border-color: oklch(0.72 0.26 310 / 0.55);
  }
  .modal-body {
    padding: 1rem 1.5rem 1.5rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }
  .shortcut-group h3 {
    margin: 0 0 0.5rem 0;
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.60 0.04 310 / 0.65);
  }
  .shortcut-list {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .shortcut-row {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    font-size: 0.82rem;
    color: oklch(0.85 0.05 310 / 0.85);
  }
  .shortcut-row kbd {
    display: inline-block;
    min-width: 5.5rem;
    text-align: center;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    padding: 0.22rem 0.5rem;
    background: oklch(0.17 0.025 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.40);
    border-radius: 4px;
    color: oklch(0.90 0.06 310);
    white-space: nowrap;
  }
  .no-results {
    margin: 0;
    font-size: 0.82rem;
    color: oklch(0.55 0.04 310 / 0.55);
    text-align: center;
    padding: 1rem 0;
  }
</style>
